import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { Close as CloseIcon, ContentCopy as CopyIcon, CheckCircle } from "@mui/icons-material";
import { useTheme } from "../../context/ThemeContext";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import config from "../../config/environment";

const AgentAPIDialog = ({ open, onClose, agentData, agentId }) => {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [copySuccess, setCopySuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  // MUI Theme
  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: "#e36250",
      },
    },
    typography: {
      fontFamily: '"Poppins", "sans-serif"',
    },
  });

  // Get API key and user ID from localStorage
  const apiKey = localStorage.getItem("api_key") || "sk-default-xxxxx";
  const apiBaseURL = config.API_BASE_URL;

  // Generate session ID
  const generateSessionId = () => {
    return agentId + "-" + Math.random().toString(36).substr(2, 9);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setCopySuccess(true);
      setTimeout(() => {
        setCopied(false);
        setCopySuccess(false);
      }, 2000);
    });
  };

  const handleCloseCopySnackbar = () => {
    setCopySuccess(false);
  };

  // Format Agent JSON
  const formatAgentJSON = () => {
    if (!agentData) return "{}";

    return JSON.stringify(agentData, null, 2);
  };

  // Format Inference cURL command
  const formatInferenceCURL = () => {
    const sessionId = generateSessionId();

    // Build cURL command dynamically
    let curlCommand = `curl -X POST '${apiBaseURL}/dynamic-agent/execute' \\\n  -H 'x-api-key: ${apiKey}' \\\n  -F 'agent_id=${agentId}' \\\n  -F 'query=Your query here' \\\n  -F 'session_id=${sessionId}' \\\n  -F 'streaming=false'`;

    // Collect all variables (both regular and chained)
    const allVariables = new Set();
    
    // Add regular variables
    if (agentData?.variables) {
      const variablesArray = typeof agentData.variables === 'string'
        ? agentData.variables.split(',').map(v => v.trim()).filter(v => v)
        : [];
      variablesArray.forEach(v => allVariables.add(v));
    }
    
    // Add chained agent variables
    const chainedVarsString = agentData?.chained_variables || agentData?.agent_chaining?.chained_variables || '';
    if (chainedVarsString) {
      const chainedVarsArray = typeof chainedVarsString === 'string'
        ? chainedVarsString.split(',').map(v => v.trim()).filter(v => v)
        : [];
      chainedVarsArray.forEach(v => allVariables.add(v));
    }

    // Add variables to cURL command if any exist
    if (allVariables.size > 0) {
      const variablesArray = Array.from(allVariables);
      const variablesObj = {};
      variablesArray.forEach(varName => {
        variablesObj[varName] = `<${varName}_value>`;
      });

      curlCommand += ` \\\n  -F 'variables=${JSON.stringify(variablesObj)}'`;
      curlCommand += `\n\n# ⚠️ REQUIRED: All variables above are REQUIRED parameters.`;
      curlCommand += `\n#   You must provide valid values for all variables (including chained agent variables) before executing.`;
    }

    // Add optional parameters as comments
    curlCommand += `\n\n# Optional parameters (uncomment to use):\n# -F 'user_location=Your Location'\n# -F 'user_timezone=Your/Timezone'\n# -F 'files=@/path/to/file.pdf'`;

    return curlCommand;
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
            color: isDarkMode ? "#ffffff" : "#111827",
            borderRadius: "16px",
            minHeight: "600px",
            maxHeight: "85vh",
            fontFamily: '"Poppins", sans-serif',
            boxShadow: isDarkMode
              ? "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
              : "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: isDarkMode ? "1px solid #404040" : "1px solid #e5e7eb",
            padding: "20px 32px",
            backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              fontSize: "20px",
              fontFamily: '"Poppins", sans-serif',
              color: isDarkMode ? "#ffffff" : "#111827",
            }}
          >
            Agent API
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <Button
              variant={copied ? "contained" : "outlined"}
              startIcon={copied ? <CheckCircle /> : <CopyIcon />}
              onClick={() => {
                if (activeTab === 0) {
                  handleCopy(formatAgentJSON());
                } else {
                  handleCopy(formatInferenceCURL());
                }
              }}
              sx={{
                textTransform: "none",
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 500,
                fontSize: "14px",
                padding: "8px 20px",
                borderRadius: "8px",
                borderColor: "#e36250",
                color: copied ? "#ffffff" : "#e36250",
                backgroundColor: copied ? "#e36250" : "transparent",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#d95545",
                  backgroundColor: copied ? "#d95545" : "rgba(227, 98, 80, 0.08)",
                },
              }}
            >
              {copied ? "Copied!" : "Copy"}
            </Button>
            <IconButton
              onClick={onClose}
              size="medium"
              sx={{
                color: isDarkMode ? "#9ca3af" : "#6b7280",
                "&:hover": {
                  backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {/* Tabs */}
          <Box
            sx={{
              borderBottom: isDarkMode ? "1px solid #404040" : "1px solid #e5e7eb",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#f9fafb",
              paddingX: "32px",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                minHeight: "56px",
                "& .MuiTab-root": {
                  textTransform: "none",
                  minHeight: "56px",
                  fontSize: "15px",
                  fontWeight: 500,
                  fontFamily: '"Poppins", sans-serif',
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  paddingX: "24px",
                  transition: "all 0.2s ease",
                  "&.Mui-selected": {
                    color: "#e36250",
                    fontWeight: 600,
                  },
                  "&:hover": {
                    color: isDarkMode ? "#ffffff" : "#111827",
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#e36250",
                  height: "3px",
                  borderRadius: "3px 3px 0 0",
                },
              }}
            >
              <Tab label="Agent JSON" />
              <Tab label="Inference" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              padding: "32px",
              backgroundColor: isDarkMode ? "#0d0d0d" : "#f8f9fa",
              "&::-webkit-scrollbar": {
                width: "10px",
              },
              "&::-webkit-scrollbar-track": {
                background: isDarkMode ? "#1a1a1a" : "#f1f1f1",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: isDarkMode ? "#404040" : "#c1c1c1",
                borderRadius: "10px",
                "&:hover": {
                  background: isDarkMode ? "#525252" : "#a8a8a8",
                },
              },
            }}
          >
            {activeTab === 0 && (
              <Box
                component="pre"
                sx={{
                  margin: 0,
                  padding: "24px",
                  backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
                  borderRadius: "12px",
                  border: isDarkMode ? "1px solid #404040" : "1px solid #e5e7eb",
                  fontSize: "14px",
                  lineHeight: "1.7",
                  fontFamily: '"Fira Code", "Consolas", "Monaco", "Courier New", monospace',
                  color: isDarkMode ? "#e5e7eb" : "#1f2937",
                  overflowX: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  boxShadow: isDarkMode
                    ? "0 4px 6px -1px rgba(0, 0, 0, 0.3)"
                    : "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                  "&::-webkit-scrollbar": {
                    height: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: isDarkMode ? "#1a1a1a" : "#f1f1f1",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: isDarkMode ? "#404040" : "#c1c1c1",
                    borderRadius: "4px",
                    "&:hover": {
                      background: isDarkMode ? "#525252" : "#a8a8a8",
                    },
                  },
                }}
              >
                {formatAgentJSON()}
              </Box>
            )}

            {activeTab === 1 && (
              <Box
                component="pre"
                sx={{
                  margin: 0,
                  padding: "24px",
                  backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
                  borderRadius: "12px",
                  border: isDarkMode ? "1px solid #404040" : "1px solid #e5e7eb",
                  fontSize: "14px",
                  lineHeight: "1.7",
                  fontFamily: '"Fira Code", "Consolas", "Monaco", "Courier New", monospace',
                  color: isDarkMode ? "#e5e7eb" : "#1f2937",
                  overflowX: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  boxShadow: isDarkMode
                    ? "0 4px 6px -1px rgba(0, 0, 0, 0.3)"
                    : "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                  "&::-webkit-scrollbar": {
                    height: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: isDarkMode ? "#1a1a1a" : "#f1f1f1",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: isDarkMode ? "#404040" : "#c1c1c1",
                    borderRadius: "4px",
                    "&:hover": {
                      background: isDarkMode ? "#525252" : "#a8a8a8",
                    },
                  },
                }}
              >
                {formatInferenceCURL()}
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={handleCloseCopySnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseCopySnackbar}
          severity="success"
          icon={<CheckCircle />}
          sx={{
            fontFamily: '"Poppins", sans-serif',
            fontWeight: 500,
            fontSize: "14px",
            backgroundColor: isDarkMode ? "#065f46" : "#d1fae5",
            color: isDarkMode ? "#ffffff" : "#065f46",
            borderRadius: "10px",
            boxShadow: isDarkMode
              ? "0 10px 15px -3px rgba(0, 0, 0, 0.3)"
              : "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
            "& .MuiAlert-icon": {
              color: isDarkMode ? "#10b981" : "#059669",
            },
          }}
        >
          Copied to clipboard!
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default AgentAPIDialog;
